import { useState, useCallback, useEffect } from 'react'
import {
  VersionReportPageConfig,
  VersionReportPageType,
  VersionReportVersionPageEntity,
} from '../../../../../../../domain/entity/version-report/VersionReportVersionEntity'
import { VersionReportVersionPageFormModel } from '../model/VersionReportVersionPageFormModel'

const createDefaultConfig = (
  pageType: VersionReportPageType
): VersionReportPageConfig => {
  switch (pageType) {
    case 'PROGRESS_OVERVIEW':
      return {
        type: 'PROGRESS_OVERVIEW',
        tableRows: [],
      }
    case 'PROGRESS_REPORT_BY_PROCESS':
      return {
        type: 'PROGRESS_REPORT_BY_PROCESS',
        tableRows: [],
      }
    case 'PROGRESS_REPORT_BY_TICKET_LIST':
      return {
        type: 'PROGRESS_REPORT_BY_TICKET_LIST',
        tableRows: [],
      }
    case 'PROGRESS_REPORT_BY_TEAM':
      return {
        type: 'PROGRESS_REPORT_BY_TEAM',
        scopeType: 'PROCESS',
        rootUuid: undefined,
        tableRows: [],
      }
  }
}

const reassignPageNumbers = (
  pages: VersionReportVersionPageFormModel[]
): Map<number, VersionReportVersionPageFormModel> => {
  const reorderedMap = new Map<number, VersionReportVersionPageFormModel>()
  pages.forEach((entity, index) => {
    const newPageNumber = index + 1
    reorderedMap.set(newPageNumber, { ...entity, pageNumber: newPageNumber })
  })
  return reorderedMap
}

const calculateNextSelectedPage = (
  currentSelection: number | null,
  deletedPageNumber: number,
  remainingPagesCount: number
): number | null => {
  if (remainingPagesCount === 0) {
    return null
  }

  if (currentSelection !== deletedPageNumber) {
    if (currentSelection! < deletedPageNumber) {
      return currentSelection
    }
    return currentSelection! - 1
  }

  return Math.min(deletedPageNumber, remainingPagesCount)
}

export const useVersionPageEntityMap = (
  initialPages?: VersionReportVersionPageEntity[],
  initialSelectedPageNumber?: number
) => {
  const [pageEntityMap, setPageEntityMap] = useState<
    Map<number, VersionReportVersionPageFormModel>
  >(new Map())
  const [nextPageNumber, setNextPageNumber] = useState(1)
  const [currentSelectedPageNumber, setCurrentSelectedPageNumber] = useState<
    number | null
  >(null)
  const [lastInitialPages, setLastInitialPages] = useState<
    VersionReportVersionPageEntity[] | undefined
  >()

  useEffect(() => {
    if (!initialPages) {
      if (lastInitialPages !== undefined) {
        setLastInitialPages(undefined)
      }
      return
    }
    if (initialPages === lastInitialPages) return
    const map = new Map<number, VersionReportVersionPageFormModel>()
    initialPages.forEach(page => {
      map.set(page.pageNumber, {
        uuid: page.uuid,
        lockVersion: page.lockVersion,
        pageNumber: page.pageNumber,
        pageType: page.pageType,
        pageTitle: page.pageTitle,
        leadSentence: page.leadSentence,
        config: page.config,
        rows: page.rows,
      })
    })
    setPageEntityMap(map)
    setNextPageNumber(
      initialPages.length > 0
        ? Math.max(...initialPages.map(p => p.pageNumber)) + 1
        : 1
    )

    const hasInitialSelection =
      initialSelectedPageNumber !== undefined &&
      initialPages.some(p => p.pageNumber === initialSelectedPageNumber)
    setCurrentSelectedPageNumber(
      initialPages.length > 0
        ? hasInitialSelection
          ? initialSelectedPageNumber
          : 1
        : null
    )
    setLastInitialPages(initialPages)
  }, [initialPages, lastInitialPages, initialSelectedPageNumber])

  const currentPageEntity = currentSelectedPageNumber
    ? pageEntityMap.get(currentSelectedPageNumber)
    : null

  const addPageEntity = useCallback(
    (pageType: VersionReportPageType, initialPageTitle: string): number => {
      const newPageNumber = nextPageNumber
      const newPageEntity: VersionReportVersionPageFormModel = {
        uuid: null,
        lockVersion: null,
        pageTitle: initialPageTitle,
        pageNumber: newPageNumber,
        pageType,
        leadSentence: undefined,
        config: createDefaultConfig(pageType),
        rows: [],
      }

      setPageEntityMap(prev => new Map(prev.set(newPageNumber, newPageEntity)))
      setCurrentSelectedPageNumber(newPageNumber)
      setNextPageNumber(prev => prev + 1)
      return newPageNumber
    },
    [nextPageNumber]
  )

  const updatePageEntity = useCallback(
    (
      pageNumber: number,
      updater: (
        prev: VersionReportVersionPageFormModel
      ) => VersionReportVersionPageFormModel
    ) => {
      setPageEntityMap(prev => {
        const entity = prev.get(pageNumber)
        if (!entity) return prev

        const updated = updater(entity)
        const newMap = new Map(prev)
        newMap.set(pageNumber, updated)
        return newMap
      })
    },
    []
  )

  const deletePageEntity = useCallback(
    (pageNumber: number) => {
      setPageEntityMap(prev => {
        const entity = prev.get(pageNumber)
        if (!entity) return prev

        const newMap = new Map(prev)
        newMap.delete(pageNumber)

        if (newMap.size === 0) {
          setCurrentSelectedPageNumber(null)
          setNextPageNumber(1)
          return new Map()
        }

        const sortedPages = Array.from(newMap.values()).sort(
          (a, b) => a.pageNumber - b.pageNumber
        )
        const reorderedMap = reassignPageNumbers(sortedPages)

        const newSelection = calculateNextSelectedPage(
          currentSelectedPageNumber,
          pageNumber,
          reorderedMap.size
        )
        setCurrentSelectedPageNumber(newSelection)
        setNextPageNumber(reorderedMap.size + 1)

        return reorderedMap
      })
    },
    [currentSelectedPageNumber]
  )

  const reorderPages = useCallback(
    (reorderedPages: VersionReportVersionPageFormModel[]) => {
      const reorderedMap = reassignPageNumbers(reorderedPages)
      setPageEntityMap(reorderedMap)

      if (currentSelectedPageNumber) {
        const currentEntity = pageEntityMap.get(currentSelectedPageNumber)
        if (currentEntity) {
          const newPageNumber =
            reorderedPages.findIndex(page => page === currentEntity) + 1

          if (newPageNumber > 0) {
            setCurrentSelectedPageNumber(newPageNumber)
          }
        }
      }
    },
    [currentSelectedPageNumber, pageEntityMap]
  )

  const resetPageEntityMap = useCallback(() => {
    setPageEntityMap(new Map())
    setNextPageNumber(1)
    setCurrentSelectedPageNumber(null)
    setLastInitialPages(undefined)
  }, [])

  return {
    pageEntityMap,
    currentPageEntity,
    currentSelectedPageNumber,
    setCurrentSelectedPageNumber,
    addPageEntity,
    updatePageEntity,
    deletePageEntity,
    reorderPages,
    resetPageEntityMap,
  }
}
